import React, { useState } from 'react';
import { Modal } from '../../../shared/components/Modal';
import { CoverTemplate, CoverCategory } from '../../../templates/types';
import { COVER_TEMPLATES } from '../../../templates/cover';
import { TemplateCard } from './TemplateCard';
import { Search } from 'lucide-react';

interface TemplateSelectorModalProps {
  isOpen: boolean;
  selectedTemplateId: string;
  onClose: () => void;
  onSelectTemplate: (template: CoverTemplate) => void;
}

export const TemplateSelectorModal: React.FC<TemplateSelectorModalProps> = ({
  isOpen,
  selectedTemplateId,
  onClose,
  onSelectTemplate,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<CoverCategory | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const categories: Array<{ id: CoverCategory | 'all'; label: string }> = [
    { id: 'all', label: 'All Templates' },
    { id: 'assignment', label: 'Assignments' },
    { id: 'lab_report', label: 'Lab Reports' },
    { id: 'project', label: 'Projects & Thesis' },
    { id: 'general_academic', label: 'General Academic' },
  ];

  const filtered = COVER_TEMPLATES.filter((tpl) => {
    const matchesCat = selectedCategory === 'all' || tpl.category === selectedCategory;
    const matchesSearch =
      tpl.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tpl.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCat && matchesSearch;
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Select Academic Cover Page Template"
      maxWidth="840px"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {/* Search & Category Filter */}
        <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ position: 'relative', width: '260px' }}>
            <Search size={15} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--text-tertiary)' }} />
            <input
              type="text"
              className="form-input text-xs"
              style={{ paddingLeft: '32px' }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search templates..."
            />
          </div>

          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {categories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => setSelectedCategory(cat.id)}
                className={`btn ${selectedCategory === cat.id ? 'btn-primary' : 'btn-secondary'} btn-sm text-xs`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Template Cards Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
            gap: 'var(--space-3)',
            maxHeight: '60vh',
            overflowY: 'auto',
            paddingRight: 4,
          }}
        >
          {filtered.map((tpl) => (
            <TemplateCard
              key={tpl.id}
              template={tpl}
              isSelected={tpl.id === selectedTemplateId}
              onSelect={(selected) => {
                onSelectTemplate(selected);
                onClose();
              }}
            />
          ))}
        </div>
      </div>
    </Modal>
  );
};
